/** Zoom-and-pan arithmetic of the roof report viewer (components/ReportModal.tsx), kept free of the DOM so it can be
 * tested with node --test. A view is a scale (1 = the whole image fits the stage) and the offset of the image's
 * centre from the stage's centre, in stage pixels. */

export const MAX_ZOOM = 6;
/** Past this zoom the viewer swaps the 1600 px preview for the full-size image. */
export const FULL_AT_ZOOM = 1.5;

export interface View {
  scale: number;
  x: number;
  y: number;
}
export interface Size {
  w: number;
  h: number;
}

export const FIT: View = { scale: 1, x: 0, y: 0 };

/** The image's size at scale 1: as large as fits the stage, aspect kept. */
export function fitSize(stage: Size, natural: Size): Size {
  const k = Math.min(stage.w / natural.w, stage.h / natural.h);
  return Number.isFinite(k) && k > 0 ? { w: natural.w * k, h: natural.h * k } : { w: 0, h: 0 };
}

/** Keeps the scale in [1, MAX_ZOOM] and the image covering the stage (centred along an axis where it is smaller). */
export function clampView(view: View, stage: Size, fitted: Size): View {
  const scale = Math.min(Math.max(view.scale, 1), MAX_ZOOM);
  const maxX = Math.max((fitted.w * scale - stage.w) / 2, 0);
  const maxY = Math.max((fitted.h * scale - stage.h) / 2, 0);
  return { scale, x: Math.min(Math.max(view.x, -maxX), maxX), y: Math.min(Math.max(view.y, -maxY), maxY) };
}

/** Zooms to `scale`, keeping the stage point (px, py), relative to the stage centre, still. */
export function zoomAt(view: View, scale: number, px: number, py: number, stage: Size, fitted: Size): View {
  const s = Math.min(Math.max(scale, 1), MAX_ZOOM);
  const k = s / view.scale;
  return clampView({ scale: s, x: px - (px - view.x) * k, y: py - (py - view.y) * k }, stage, fitted);
}

/** The single CSS transform of the image layer (transform-origin 0 0, the layer sized `fitted`). */
export function layerTransform(view: View, stage: Size, fitted: Size): string {
  const tx = stage.w / 2 + view.x - (fitted.w * view.scale) / 2;
  const ty = stage.h / 2 + view.y - (fitted.h * view.scale) / 2;
  return `translate3d(${tx.toFixed(2)}px, ${ty.toFixed(2)}px, 0) scale(${view.scale.toFixed(4)})`;
}

/** Whether the preview (`previewWidth` px) is no longer enough: zoomed past FULL_AT_ZOOM, or (high-DPI screens)
 * shown at more than twice its pixel size. */
export function needsFull(scale: number, fittedWidth: number, dpr: number, previewWidth: number): boolean {
  return scale >= FULL_AT_ZOOM || scale * fittedWidth * dpr > 2 * previewWidth;
}
